import { usePlatformPermissions } from '../usePlatformPermissions';
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { colors } from '../theme';
import { StatusDot } from './StatusDot';
import { EnvironmentPill } from './EnvironmentPill';
import { DeleteApplicationDialog } from './DeleteApplicationDialog';
import { RedeployApplicationDialog } from './RedeployApplicationDialog';
import { relativeTime } from '../relativeTime';
import { humanizeSlug } from '../nameUtils';
import { useIdentity } from '../useIdentity';
import { deleteApplication, redeployApplication, updateApplicationSource, getWorkflowStatus } from '../api';
import { toRequestSource, type PickedSource } from '../uploadUtils';
import type { ApplicationSummary } from '../types';

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '10px 16px',
  fontSize: 12,
  fontWeight: 600,
  color: colors.textMuted,
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: 14,
  borderBottom: `1px solid ${colors.borderLight}`,
  color: colors.text,
  whiteSpace: 'nowrap',
};

/**
 * Real bug found live (screenshot: only a sliver of the menu peeking out at
 * the bottom of the table): a `position: absolute` dropdown nested inside
 * the table's `overflowX: auto` wrapper (and the outer `overflow: hidden`
 * card on the homepage) gets clipped — setting only overflow-x non-visible
 * forces overflow-y to also become non-visible per the CSS spec, so there's
 * no way to keep the horizontal scroll and let the menu escape vertically
 * from within that DOM position. Rendered through a portal instead, position
 * computed from the trigger button's own bounding rect, so it escapes every
 * ancestor's overflow/clipping entirely.
 */
function RowActions({
  app,
  canDelete,
  canEdit,
  onRequestDelete,
  onRequestRedeploy,
}: {
  app: ApplicationSummary;
  /** Delete is allowlisted server-side; editing/redeploy is open to all company users. */
  canDelete: boolean;
  canEdit: boolean;
  onRequestDelete: (app: ApplicationSummary) => void;
  onRequestRedeploy: (app: ApplicationSummary) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Actions for ${app.name}`}
        aria-expanded={open}
        onClick={toggle}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 18,
          color: colors.textMuted,
          padding: '2px 8px',
          borderRadius: 6,
        }}
      >
        &hellip;
      </button>
      {open &&
        menuPos &&
        createPortal(
          <>
            <div
              // Click-outside-to-close catcher, full viewport, behind the menu.
              onClick={() => setOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 100 }}
            />
            <div
              role="menu"
              style={{
                position: 'fixed',
                top: menuPos.top,
                right: menuPos.right,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)',
                zIndex: 101,
                minWidth: 140,
              }}
            >
              <Link
                role="menuitem"
                to={`/applications/${app.id}`}
                style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: colors.text, textDecoration: 'none' }}
                onClick={() => setOpen(false)}
              >
                View Details
              </Link>
              {app.url && (
                <a
                  role="menuitem"
                  href={app.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: colors.text, textDecoration: 'none' }}
                  onClick={() => setOpen(false)}
                >
                  Open Application
                </a>
              )}
              {canEdit && (
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onRequestRedeploy(app);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontSize: 13,
                    color: colors.text,
                    background: 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${colors.borderLight}`,
                    cursor: 'pointer',
                  }}
                >
                  Edit environment &amp; redeploy
                </button>
              )}
              {/* Rendered only for users the SERVER said may delete (see
                  getPlatformPermissions). Hiding it is presentation only —
                  platform-controller re-checks the allowlist on the request
                  itself, which is the actual enforcement. */}
              {canDelete && (
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onRequestDelete(app);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontSize: 13,
                    color: colors.danger,
                    background: 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${colors.borderLight}`,
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

function SimpleAppRows({
  apps,
  canDelete,
  canEdit,
  onRequestDelete,
  onRequestRedeploy,
}: {
  apps: ApplicationSummary[];
  canDelete: boolean;
  canEdit: boolean;
  onRequestDelete: (app: ApplicationSummary) => void;
  onRequestRedeploy: (app: ApplicationSummary) => void;
}) {
  const navigate = useNavigate();
  return (
    <div>
      {apps.map((app, i) => {
        const ok = app.status === 'Running';
        const subtitle = [app.type, app.department ? humanizeSlug(app.department) : null].filter(Boolean).join(' · ');
        return (
          <div
            key={app.id}
            style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderTop: i === 0 ? 'none' : `1px solid ${colors.borderLight}` }}
          >
            <div
              aria-hidden="true"
              style={{ width: 40, height: 40, borderRadius: 10, background: ok ? '#dbeafe' : '#fee2e2', color: ok ? '#2563eb' : colors.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}
            >
              {app.name.charAt(0).toUpperCase()}
            </div>
            <Link to={`/applications/${app.id}`} style={{ flex: 1, minWidth: 0, textDecoration: 'none' }}>
              <div style={{ fontWeight: 600, color: colors.text, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.name}</div>
              <div style={{ fontSize: 13, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
            </Link>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 140, fontSize: 13, color: ok ? colors.success : colors.danger, fontWeight: 500 }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? colors.success : colors.danger }} />
              {ok ? 'Ready' : 'Needs attention'}
            </span>
            {ok && app.url ? (
              <a
                href={app.url}
                target="_blank"
                rel="noreferrer"
                style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: colors.primary, textDecoration: 'none', background: colors.surface }}
              >
                Open app &#x2197;
              </a>
            ) : (
              <button
                type="button"
                // Most failures are a missing/wrong setting, which the
                // edit-environment dialog fixes; people without that
                // permission land on the details page (logs) instead.
                onClick={() => (canEdit ? onRequestRedeploy(app) : navigate(`/applications/${app.id}`))}
                style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: colors.primary, background: colors.surface, cursor: 'pointer' }}
              >
                Fix issue
              </button>
            )}
            <RowActions app={app} canDelete={canDelete} canEdit={canEdit} onRequestDelete={onRequestDelete} onRequestRedeploy={onRequestRedeploy} />
          </div>
        );
      })}
    </div>
  );
}

export function ApplicationsTable({
  applications: unsorted,
  canDelete = false,
  onDeleted,
  onRedeployed,
  variant = 'table',
}: {
  /** 'simple' = the friendly home-page list for non-technical users. */
  variant?: 'table' | 'simple';
  applications: ApplicationSummary[];
  /** Comes from the server's own allowlist answer, never from a local list. */
  canDelete?: boolean;
  /** Called once a delete workflow has actually reported DELETED, so the
   * caller can refresh — the row is not removed optimistically. */
  onDeleted?: (app: ApplicationSummary) => void;
  /** Called once a redeploy has actually reported REDEPLOYED, so the caller can
   * refresh timestamps. The row is not mutated optimistically. */
  onRedeployed?: (app: ApplicationSummary) => void;
}) {
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const { email } = useIdentity();
  const { canEdit } = usePlatformPermissions();

  // One or many apps queued for the delete confirmation dialog — a single
  // row's own Delete action queues one, "Delete selected" queues the whole
  // checkbox selection. Same dialog, same delete loop, either way.
  const [pending, setPending] = useState<ApplicationSummary[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [redeploying, setRedeploying] = useState<ApplicationSummary | null>(null);
  const [redeployBusy, setRedeployBusy] = useState(false);
  const [redeployStage, setRedeployStage] = useState<string | null>(null);
  const [redeployError, setRedeployError] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  const applications = [...unsorted].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  // Drop any selected id that no longer corresponds to a row — most often
  // because it was just deleted, but also covers a filtered/refetched list.
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(unsorted.map((a) => a.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsorted]);

  const allSelected = applications.length > 0 && applications.every((a) => selected.has(a.id));
  const someSelected = selected.size > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(applications.map((a) => a.id)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * A 2xx from DELETE only means the workflow started, so this polls
   * getWorkflowStatus until it reports a terminal state and only then tells
   * the caller an app is gone. Reporting success on the 202 would show the
   * row vanishing while the Coolify call could still fail.
   *
   * Runs one app at a time (not Promise.all) so `deleteProgress` means
   * something and a stuck workflow doesn't tie up every other delete's poll
   * loop behind it. On a partial bulk failure, only the apps that actually
   * failed stay in `pending` — a retry (clicking the confirm button again
   * without closing the dialog) re-attempts just those, not ones already gone.
   */
  const confirmDelete = async () => {
    if (!pending || pending.length === 0) return;
    setDeleting(true);
    setDeleteError(null);
    setDeleteProgress({ done: 0, total: pending.length });

    const stillPending: ApplicationSummary[] = [];
    const failures: string[] = [];

    for (const app of pending) {
      try {
        const { workflowId } = await deleteApplication(identityApi, email, app.id, app.name);

        // Bounded so a stuck workflow surfaces as an error instead of spinning
        // forever: 2s interval, 60 attempts = a 2 minute ceiling, matching the
        // Coolify activity's own startToCloseTimeout.
        let succeeded = false;
        for (let attempt = 0; attempt < 60; attempt++) {
          if (cancelled.current) return;
          const res = await getWorkflowStatus(identityApi, workflowId);
          if (res.result) {
            const finalStatus = String((res.result as { status?: unknown }).status ?? '');
            if (finalStatus === 'DELETED') {
              succeeded = true;
            } else {
              throw new Error(
                String((res.result as { failureMessage?: unknown }).failureMessage ?? 'delete failed'),
              );
            }
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        if (!succeeded) throw new Error('delete is taking longer than expected — check the Temporal workflow');

        if (cancelled.current) return;
        onDeleted?.(app);
        setSelected((prev) => {
          if (!prev.has(app.id)) return prev;
          const next = new Set(prev);
          next.delete(app.id);
          return next;
        });
      } catch (err) {
        stillPending.push(app);
        failures.push(pending.length > 1 ? `${app.name}: ${(err as Error).message}` : (err as Error).message);
      }
      if (cancelled.current) return;
      setDeleteProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }

    if (cancelled.current) return;
    setDeleting(false);
    setDeleteProgress(null);
    if (failures.length > 0) {
      setPending(stillPending);
      setDeleteError(failures.join('\n'));
    } else {
      setPending(null);
    }
  };

  /**
   * A rebuild clones, builds and pushes an image, so this polls far longer
   * than the delete flow: 5s interval, 120 attempts = a 10 minute ceiling,
   * matching buildAndPushImageActivity's own 10 minute timeout. Reporting
   * success on the 2xx would claim the new version is live while the build is
   * still running.
   */
  const confirmRedeploy = async (payload: {
    envVars?: Record<string, string>;
    removeEnvKeys?: string[];
    rebuildFromRepo: boolean;
    addDatabase?: 'postgres' | 'mysql';
    /** A new version picked from the user's computer ("Update app"). */
    newSource?: PickedSource;
  }) => {
    if (!redeploying) return;
    setRedeployBusy(true);
    setRedeployError(null);
    setRedeployStage(payload.newSource ? 'UPLOADING_NEW_VERSION' : null);
    try {
      const { newSource, ...redeployPayload } = payload;
      const { workflowId } = newSource
        ? await updateApplicationSource(
            identityApi,
            email,
            redeploying.id,
            redeploying.name,
            await toRequestSource(newSource),
            {
              envVars: payload.envVars,
              removeEnvKeys: payload.removeEnvKeys,
              addDatabase: payload.addDatabase,
            },
          )
        : await redeployApplication(identityApi, email, redeploying.id, redeploying.name, redeployPayload);
      for (let attempt = 0; attempt < 120; attempt++) {
        if (cancelled.current) return;
        const res = await getWorkflowStatus(identityApi, workflowId);
        if (res.result) {
          const finalStatus = String((res.result as { status?: unknown }).status ?? '');
          if (finalStatus === 'REDEPLOYED') {
            if (cancelled.current) return;
            setRedeployBusy(false);
            setRedeploying(null);
            setRedeployStage(null);
            onRedeployed?.(redeploying);
            return;
          }
          throw new Error(
            String((res.result as { failureMessage?: unknown }).failureMessage ?? 'redeploy failed'),
          );
        }
        setRedeployStage(res.status);
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
      throw new Error('redeploy is taking longer than expected — check the Temporal workflow');
    } catch (err) {
      if (cancelled.current) return;
      setRedeployBusy(false);
      setRedeployStage(null);
      setRedeployError((err as Error).message);
    }
  };

  if (applications.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
        No apps yet. Drop your project above to put your first app online.
      </div>
    );
  }

  return (
    <div>
      {canDelete && selected.size > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: colors.bg,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
            {selected.size} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: colors.surface,
                color: colors.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteError(null);
                setPending(applications.filter((a) => selected.has(a.id)));
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                background: colors.danger,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Delete selected
            </button>
          </div>
        </div>
      )}
      {variant === 'simple' ? (
        <SimpleAppRows
          apps={applications}
          canDelete={canDelete}
          canEdit={canEdit}
          onRequestDelete={(a) => {
            setDeleteError(null);
            setPending([a]);
          }}
          onRequestRedeploy={setRedeploying}
        />
      ) : (
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
        <thead>
          <tr>
            {canDelete && (
              <th style={{ ...thStyle, width: 36 }}>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all applications"
                />
              </th>
            )}
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Department</th>
            <th style={thStyle}>Source</th>
            <th style={thStyle}>Environment</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Updated</th>
            <th style={thStyle}>URL</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {applications.map((app) => (
            <tr
              key={app.id}
              onClick={() => navigate(`/applications/${app.id}`)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = colors.bg;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              {canDelete && (
                <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(app.id)}
                    onChange={() => toggleOne(app.id)}
                    aria-label={`Select ${app.name}`}
                  />
                </td>
              )}
              <td style={tdStyle}>
                <Link
                  to={`/applications/${app.id}`}
                  style={{ color: colors.text, fontWeight: 500, textDecoration: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {app.name}
                </Link>
              </td>
              <td style={tdStyle}>{app.type}</td>
              <td style={tdStyle}>
                {app.department ? humanizeSlug(app.department) : <span style={{ color: colors.textMuted }}>&mdash;</span>}
              </td>
              <td style={{ ...tdStyle, fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {app.repoFullName ? (
                  <a
                    href={`https://github.com/${app.repoFullName}${app.branch ? `/tree/${app.branch}` : ''}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: colors.primary, textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {app.repoFullName.split('/').pop()}
                    {app.branch ? <span style={{ color: colors.textMuted }}>@{app.branch}</span> : null}
                  </a>
                ) : (
                  <span style={{ color: colors.textMuted }}>&mdash;</span>
                )}
              </td>
              <td style={tdStyle}>
                <EnvironmentPill environment={app.environment} />
              </td>
              <td style={tdStyle}>
                <StatusDot status={app.status} />
              </td>
              <td style={{ ...tdStyle, color: colors.textMuted }}>{relativeTime(app.updatedAt)}</td>
              <td style={tdStyle}>
                {app.url ? (
                  <a
                    href={app.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: colors.primary, textDecoration: 'none', display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}
                    title={app.url}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {app.url.replace(/^https?:\/\//, '')}
                  </a>
                ) : (
                  <span style={{ color: colors.textMuted }}>&mdash;</span>
                )}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                <RowActions
                  app={app}
                  canDelete={canDelete}
                  canEdit={canEdit}
                  onRequestDelete={(a) => {
                    setDeleteError(null);
                    setPending([a]);
                  }}
                  onRequestRedeploy={setRedeploying}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
      {redeploying && (
        <RedeployApplicationDialog
          app={redeploying}
          busy={redeployBusy}
          stage={redeployStage}
          error={redeployError}
          onCancel={() => {
            setRedeploying(null);
            setRedeployError(null);
            setRedeployStage(null);
          }}
          onConfirm={confirmRedeploy}
        />
      )}
      {pending && pending.length > 0 && (
        <DeleteApplicationDialog
          apps={pending}
          busy={deleting}
          progress={deleteProgress ?? undefined}
          error={deleteError}
          onCancel={() => {
            setPending(null);
            setDeleteError(null);
            setDeleteProgress(null);
          }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
